// Lib
import { FC, useEffect, useState } from "react";
// Api
import { useGetModifiersQuery } from "rtkQuery/query/modifiersAPI";
// Hooks
import { useNotification, useTable, useViewport } from "hooks";
import { useAppSelector } from "hooks/redux";
// Selectors
import { getUserCompanyId } from "rtkQuery/selectors";
// Types
import { ETable } from "types/tableFilters";
import { Modifier } from "types/modifiers";
// Utils
import { errorHandler } from "utils/errorHandler";
// Icons
import { SearchBlackIcon } from "icons";
// Components
import { Modal, Pagination, Table } from "components";
import { Input } from "components/Form";
// Styled
import { Button } from "styled/Buttons";
import { FlexContainer } from "styled/Box";
import { Typography } from "styled/Typography";

import { columns } from "./config";

interface AddModifiersModal {
  open: boolean;
  productId: string;
  locationId: string;
  selectedModifiers: Modifier[];
  handleAddModifiers: (selected: Modifier[]) => void;
  handleClose: () => void;
}

export const AddModifiersModal: FC<AddModifiersModal> = ({
  open,
  productId,
  locationId,
  selectedModifiers,
  handleAddModifiers,
  handleClose,
}) => {
  const { isMobile, isLargeMobile, isDesktop } = useViewport();

  const { openNotification } = useNotification();

  const companyId = useAppSelector(getUserCompanyId);

  const {
    orderDirection,
    orderBy,
    currentPage,
    pageSize,
    search,
    debouncedSearch,
    setCurrentPage,
    setPageSize,
    setSearch,
    handleSort,
  } = useTable({ name: ETable.AddonModifierModal, removeQueryParams: true });

  const {
    data: tableData,
    isFetching: isTableDataFetching,
    error,
  } = useGetModifiersQuery(
    {
      companyId,
      siteId: locationId,
      query: {
        baseProductId: productId,
        page: currentPage,
        size: pageSize,
        orderBy: orderBy,
        orderDirection: orderDirection,
        query: debouncedSearch,
      },
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !companyId || !open,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  useEffect(() => {
    if (error) {
      errorHandler({ error, openNotification });
    }
  }, [error]);

  const [selected, setSelected] = useState<Modifier[]>([]);

  useEffect(() => {
    if (!open) {
      setSelected([]);
    } else {
      setSelected(selectedModifiers);
    }
  }, [open]);

  const disabledRows = selectedModifiers.map(el => el.id);

  const rowSelection = {
    getCheckboxProps: (record: Modifier) => ({
      disabled: disabledRows.includes(record.id),
    }),
    onSelect: (record: Modifier, selected: boolean) => {
      if (selected) {
        setSelected(prev => [...prev, record]);
      } else {
        setSelected(prev => prev.filter(p => p.id !== record.id));
      }
    },
    onSelectAll: (
      selected: boolean,
      _selectedRows: Modifier[],
      changeRows: Modifier[],
    ) => {
      if (selected) {
        setSelected(prev => prev.concat(changeRows));
      } else {
        setSelected(prev =>
          prev.filter(p => !changeRows.find(r => r.id === p.id)),
        );
      }
    },
  };

  const addSelectedItems = () => {
    handleAddModifiers(selected);
    handleClose();
  };

  const onClose = () => {
    setSelected([]);
    handleClose();
  };

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
    handleSort(sorter?.field, sorter?.order);
  };

  const title = `Add Addons & Modifiers`;

  return (
    <Modal title={title} open={open} width={1150} onClose={onClose}>
      <FlexContainer $fullwidth $column $gap={20}>
        <FlexContainer
          $fullwidth={isLargeMobile}
          $column={isLargeMobile}
          $gap={8}
          $align={isLargeMobile ? "stretch" : "center"}
          $justify="space-between"
        >
          <Typography.TitleThin>
            Showing {tableData?.data?.length || 0} of {tableData?.total} items
          </Typography.TitleThin>

          <Input
            prefix={<SearchBlackIcon />}
            placeholder="Search"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </FlexContainer>

        <Table
          rowKey={record => record.id}
          isLoading={isTableDataFetching}
          wrap={isLargeMobile}
          accentColumn={3}
          columns={columns}
          dataSource={tableData?.data || []}
          scroll={{ y: "55vh", x: isLargeMobile && "150vw" }}
          rowSelection={{
            selectedRowKeys: selected.map(el => el.id),
            type: "checkbox",
            ...rowSelection,
          }}
          onChange={handleTableChange}
        />

        <Pagination
          padding="12px 0"
          pageSize={pageSize}
          showSizeChanger={!isDesktop}
          currentPage={currentPage}
          setPageSize={setPageSize}
          setCurrentPage={setCurrentPage}
          totalItems={tableData?.total}
        />
      </FlexContainer>

      <FlexContainer
        $fullwidth
        $column={isMobile}
        $justify="flex-end"
        $gap={8}
        $padding="24px 0 0"
      >
        <Button.Heading $fullWidth={isMobile} onClick={onClose}>
          Cancel
        </Button.Heading>

        <Button.Heading
          type="primary"
          $fullWidth={isMobile}
          disabled={!selected.length}
          onClick={addSelectedItems}
        >
          Add Modifier
        </Button.Heading>
      </FlexContainer>
    </Modal>
  );
};
